"use client";

import { ReactNode } from "react";
import {
  RiCalendarLine,
  RiCheckboxCircleLine,
  RiGitBranchLine,
  RiMessageLine,
} from "react-icons/ri";

import IdDisplay from "@/components/ui/id-display";
import { Skeleton } from "@/components/ui/skeleton";

import { cx } from "@/lib/tailwind-utils";

import { useCarePlanContext } from "./care-plan";
import { StepType, getStepDisplayName } from "./types";

/**
 * Shared step detail header + layout used by both StepDetail and DiffStepDetail.
 *
 * Renders the step icon, display name, type label, and ID.
 * Children are rendered below the header (type-specific content).
 */
export function StepDetailLayout({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const {
    state: { selectedStep },
    lookups: { serviceById, medicationById, measurementById, isLookupsLoading },
  } = useCarePlanContext();

  if (!selectedStep) return null;

  const typeIcon =
    selectedStep.type === StepType.MESSAGE ? (
      <RiMessageLine size={14} />
    ) : selectedStep.type === StepType.TASK ? (
      <RiCheckboxCircleLine size={14} />
    ) : selectedStep.type === StepType.DECISION ? (
      <RiGitBranchLine size={14} />
    ) : (
      <RiCalendarLine size={14} />
    );

  const typeLabel =
    selectedStep.type === StepType.MESSAGE
      ? "Message"
      : selectedStep.type === StepType.TASK
        ? "Task"
        : selectedStep.type === StepType.DECISION
          ? "Decision"
          : "Appointment";

  const hasLookupDependentContent =
    selectedStep.serviceIds.length > 0 ||
    selectedStep.medicationIds.length > 0 ||
    selectedStep.measurements.length > 0;
  const showNameSkeleton =
    isLookupsLoading &&
    selectedStep.type !== StepType.MESSAGE &&
    hasLookupDependentContent;

  const displayName = getStepDisplayName(selectedStep, {
    serviceById,
    medicationById,
    measurementById,
  });

  return (
    <div className={cx("space-y-4", className)}>
      {/* Header */}
      <div className="flex items-center justify-between max-w-96">
        <div className="flex items-center gap-2 overflow-hidden">
          <span className="flex items-center justify-center rounded-md bg-decoda-blue p-1 text-primary-foreground">
            {typeIcon}
          </span>
          <div className="w-full overflow-hidden">
            {showNameSkeleton ? (
              <Skeleton className="h-4 w-40 mb-1" />
            ) : (
              <span className="text-body-strong text-foreground truncate block">
                {displayName || "Untitled Step"}
              </span>
            )}
            <span className="text-caption text-foreground-secondary">
              {typeLabel}
            </span>
          </div>
        </div>
      </div>

      <IdDisplay
        id={selectedStep.id}
        label="Step ID"
        className="text-tiny text-foreground-muted"
      />

      {children}
    </div>
  );
}
